import { apiFetch, readApiJson } from '@/lib/api-fetch'
import {
  InventoryGroupChildrenSchema,
  InventoryGroupPageSchema,
  InventoryPageSchema,
  StockMismatchSignalSchema,
  InventoryVariantSchema,
  InventoryVariantUnavailableSchema,
  ModelGroupSchema,
  ProductMediaOrderSchema,
  ProductMediaSchema,
  ProductMediaWriteSchema,
  ProductOrderHistorySchema,
  UnitListingPageSchema,
  UnitWarehouseSchema,
  VariantReconciliationSchema,
  type DeploySkewed,
  type DescriptionPresence,
  type InventoryDiscrepancyReason,
  type InventoryGroupChildren,
  type InventoryGroupLevel,
  type InventoryIncludeFacet,
  type InventoryGroupPage,
  type InventoryPage,
  type StockMismatchSignal,
  type InventoryOrderGroupSort,
  type InventoryPageSort,
  type InventorySortDirection,
  type InventoryVariant,
  type InventoryRowGrain,
  type InventoryVariantUnavailableReason,
  type LocalStockState,
  type MarketplaceBinding,
  type MarketplacePresenceState,
  type MatchBucket,
  type MissingTaxonomyFacet,
  type ModelGroup,
  type ProductMedia,
  type ProductMediaOrder,
  type ProductMediaWrite,
  type ProductOrderHistory,
  type UnitLifecycleScope,
  type UnitListingDirection,
  type UnitListingPage,
  type UnitListingSort,
  type VariantReconciliation,
  type UnitWarehouse,
  type WarehouseSource,
} from '@/lib/api/schemas/inventory.schema'
import type { YmEconomicsFilter, YmPriceAttractivenessLevel } from '@/lib/api/schemas/ymarket-pricing.schema'

export type InventoryFilters = {
  lifecycle?: UnitLifecycleScope
  search?: string
  discrepancy?: InventoryDiscrepancyReason
  staleOnly?: boolean
  material?: string
  fineness?: string
  productType?: string
  shape?: string
  techSize?: string
  manufacturer?: string
  availableOn?: string[]
  absentOn?: string[]
  presentOn?: string[]
  readyFor?: string[]
  marketplacePresence?: MarketplacePresenceState
  providerCode?: WarehouseSource
  localStock?: LocalStockState
  stockMismatch?: boolean
  matchBucket?: MatchBucket
  binding?: MarketplaceBinding
  missingAuthoritativeWeight?: boolean
  missingTaxonomy?: MissingTaxonomyFacet
  withPhotos?: boolean
  withoutPhotos?: boolean
  limit?: number
  offset?: number
  unresolvedOnly?: boolean
  missingMarking?: boolean
  unavailableOrStaleStock?: boolean
  problems?: boolean
  descriptionPresence?: DescriptionPresence
  rowGrain?: InventoryRowGrain
  orderProgram?: string[]
  orderStatus?: string[]
  orderCity?: string
  include?: InventoryIncludeFacet[]
  // Order is the server's, over the whole population. Absent means the route's
  // own default, which is the movement column newest first.
  sort?: InventoryPageSort
  direction?: InventorySortDirection
  /** A stored-snapshot Market filter the server applies before paging. */
  ymarketEconomics?: YmEconomicsFilter
  ymarketNetPerGramMin?: number
  ymarketNetPerGramMax?: number
  ymarketPriceForTargetMin?: number
  ymarketPriceForTargetMax?: number
  /** «Покупатель заплатит» on the Market showcase, whole roubles, inclusive. */
  ymarketShowcaseMin?: string
  ymarketShowcaseMax?: string
  /** The showcase without Yandex Pay over the card weight, ₽/г, inclusive. */
  ymarketShowcasePerGramMin?: number
  ymarketShowcasePerGramMax?: number
  /** «Цена на Маркете»: the Market's «Привлекательность цен» level. */
  ymarketAttractiveness?: YmPriceAttractivenessLevel
  /** Showcase ₽/г strictly below the piece's own «Порог дешёвой витрины» (cheapThreshold formula). */
  ymarketBelowThreshold?: boolean
}

export type InventoryGroupFilters = InventoryFilters & {
  lifecycle: UnitLifecycleScope
  /** The order level's column; a piece level has its own order and refuses one. */
  orderSort?: InventoryOrderGroupSort
}

const textParams = [
  'search',
  'material',
  'fineness',
  'productType',
  'shape',
  'techSize',
  'manufacturer',
  'providerCode',
  'discrepancy',
  'marketplacePresence',
  'localStock',
  'matchBucket',
  'binding',
  'missingTaxonomy',
  'rowGrain',
  'orderCity',
  'descriptionPresence',
  'ymarketEconomics',
  'ymarketShowcaseMin',
  'ymarketShowcaseMax',
  'ymarketAttractiveness',
] as const satisfies readonly (keyof InventoryFilters)[]

const flagParams = ['staleOnly', 'stockMismatch', 'missingAuthoritativeWeight', 'withPhotos', 'withoutPhotos', 'unresolvedOnly', 'missingMarking', 'unavailableOrStaleStock', 'problems', 'ymarketBelowThreshold'] as const satisfies readonly (keyof InventoryFilters)[]

export function inventoryQuery(filters: InventoryFilters = {}): URLSearchParams {
  const query = new URLSearchParams()
  for (const name of textParams) {
    const value = filters[name]
    if (typeof value === 'string' && value.trim()) query.set(name, value.trim())
  }
  for (const name of [
    'ymarketNetPerGramMin',
    'ymarketNetPerGramMax',
    'ymarketPriceForTargetMin',
    'ymarketPriceForTargetMax',
    'ymarketShowcasePerGramMin',
    'ymarketShowcasePerGramMax',
  ] as const) {
    const value = filters[name]
    if (value !== undefined && Number.isFinite(value)) query.set(name, String(value))
  }
  for (const value of new Set(
    (filters.availableOn ?? []).map((code) => code.trim()).filter(Boolean),
  )) query.append('availableOn', value)
  for (const name of [
    'absentOn',
    'presentOn',
    'readyFor',
    'orderProgram',
    'orderStatus',
  ] as const) {
    for (const value of new Set(
      (filters[name] ?? []).map((code) => code.trim()).filter(Boolean),
    )) query.append(name, value)
  }
  for (const value of new Set(filters.include ?? [])) query.append('include', value)
  for (const name of flagParams) if (filters[name] === true) query.set(name, 'true')
  if (filters.lifecycle) query.set('lifecycle', filters.lifecycle)
  if (filters.sort) query.set('sort', filters.sort)
  if (filters.direction) query.set('direction', filters.direction)
  query.set('limit', String(filters.limit ?? 50))
  query.set('offset', String(filters.offset ?? 0))
  return query
}

export async function getInventory(filters: InventoryFilters = {}, signal?: AbortSignal): Promise<InventoryPage> {
  return InventoryPageSchema.parse(
    await readApiJson(await apiFetch(`/api/inventory?${inventoryQuery(filters).toString()}`, { signal })),
  )
}

/**
 * Every count of the filter panel from one response: for each `panelCount` key,
 * what the list would total with that value chosen instead of the current one.
 */
export async function getInventoryPanelCounts(
  filters: InventoryFilters,
  keys: readonly string[],
  signal?: AbortSignal,
): Promise<Record<string, number>> {
  const query = inventoryQuery({ ...filters, include: undefined, sort: undefined, direction: undefined, limit: 1, offset: 0 })
  for (const key of new Set(keys)) query.append('panelCount', key)
  const page = InventoryPageSchema.parse(
    await readApiJson(await apiFetch(`/api/inventory?${query.toString()}`, { signal })),
  )
  return page.panelCounts ?? {}
}

export async function getStockMismatchSignal(signal?: AbortSignal): Promise<StockMismatchSignal> {
  return StockMismatchSignalSchema.parse(
    await readApiJson(await apiFetch('/api/inventory/stock-mismatches', { signal })),
  )
}

/**
 * Warehouse rows grouped BY THE SERVER. The browser sends the level and the same
 * filters the ungrouped page sends, and renders what comes back; it never folds a
 * page it already holds into groups, because a page is a window and a group row
 * is a statement about the whole result.
 */
export async function getInventoryGroups(
  level: InventoryGroupLevel,
  filters: InventoryGroupFilters,
  signal?: AbortSignal,
): Promise<InventoryGroupPage> {
  const query = inventoryGroupQuery(level, filters)
  return InventoryGroupPageSchema.parse(
    await readApiJson(await apiFetch(`/api/inventory/groups?${query.toString()}`, { signal })),
  )
}

export function inventoryGroupQuery(level: InventoryGroupLevel, filters: InventoryGroupFilters): URLSearchParams {
  const query = inventoryQuery(filters)
  query.set('level', level)
  query.set('lifecycle', filters.lifecycle)
  if (filters.orderSort) query.set('sort', filters.orderSort)
  return query
}

/**
 * One group's children, on demand. `groupKey` is the opaque key the parent row
 * published and is passed back verbatim; the client neither builds nor parses
 * one. Expanding a group issues exactly this request and never refetches the
 * page, and `total` on the response is the child POPULATION, so `Показать все N`
 * never reads N off the array it received.
 */
export async function getInventoryGroupChildren(
  groupKey: string,
  filters: InventoryGroupFilters,
  signal?: AbortSignal,
): Promise<InventoryGroupChildren> {
  const query = inventoryQuery(filters)
  query.set('groupKey', groupKey)
  query.set('lifecycle', filters.lifecycle)
  return InventoryGroupChildrenSchema.parse(
    await readApiJson(
      await apiFetch(`/api/inventory/groups/children?${query.toString()}`, { signal }),
    ),
  )
}

/** A keyed variant read the server refused, carrying its closed reason. */
export class InventoryVariantUnavailableError extends Error {
  readonly status: number
  // The published refusal set, read leniently: the sentence the operator sees
  // is the server's `message`, so a code this build does not know must still
  // carry that sentence rather than fall through to a generic failure.
  readonly reason: DeploySkewed<InventoryVariantUnavailableReason>

  constructor(status: number, reason: DeploySkewed<InventoryVariantUnavailableReason>, message: string) {
    super(message)
    this.name = 'InventoryVariantUnavailableError'
    this.status = status
    this.reason = reason
  }
}

// The same media types `readApiJson` accepts. Restated here rather than exported
// from `api-fetch`, which is shared by every client and is not being changed for
// one route; the duplication is deliberate and narrow.
function isJsonMediaType(value: string | null): boolean {
  if (value === null) return false
  const mediaType = value.split(';', 1)[0].trim().toLowerCase()
  return mediaType === 'application/json' || mediaType.endsWith('+json')
}

// Only this route answers the envelope, so only this route reads it. A body that
// does not validate is not coerced into a reason: it falls through to the generic
// path, which is what keeps a proxy error page or a truncated response from being
// rendered as a classification the server never made. Content-Type is checked
// first, so a body that merely LOOKS like the envelope is not accepted from a
// response that never claimed to be JSON.
async function inventoryVariantUnavailable(
  response: Response,
): Promise<InventoryVariantUnavailableError | null> {
  if (!isJsonMediaType(response.headers.get('content-type'))) return null
  let payload: unknown
  try {
    payload = await response.clone().json()
  } catch {
    return null
  }
  const parsed = InventoryVariantUnavailableSchema.safeParse(payload)
  return parsed.success
    ? new InventoryVariantUnavailableError(response.status, parsed.data.reason, parsed.data.message)
    : null
}

// canonicalProductId is the only durable identifier a route may carry.
export async function getInventoryVariant(canonicalProductId: string, signal?: AbortSignal): Promise<InventoryVariant> {
  const response = await apiFetch(`/api/inventory/${encodeURIComponent(canonicalProductId)}`, { signal })
  if (!response.ok) {
    const unavailable = await inventoryVariantUnavailable(response)
    if (unavailable) throw unavailable
  }
  return InventoryVariantSchema.parse(await readApiJson(response))
}

export type UnitWarehouseFilters = Pick<InventoryFilters, 'providerCode'> & {
  canonicalProductId?: string
  limit?: number
}

export async function getUnitWarehouse(
  filters: UnitWarehouseFilters = {},
  signal?: AbortSignal,
): Promise<UnitWarehouse> {
  const query = new URLSearchParams()
  if (filters.providerCode?.trim()) query.set('providerCode', filters.providerCode.trim())
  if (filters.canonicalProductId?.trim()) query.set('productId', filters.canonicalProductId.trim())
  query.set('limit', String(filters.limit ?? 100))
  return UnitWarehouseSchema.parse(
    await readApiJson(await apiFetch(`/api/inventory/units?${query.toString()}`, { signal })),
  )
}

/**
 * The ungrouped Warehouse table: one physical piece per row.
 *
 * Order, lifecycle scope and paging are the server's decision. Nothing here
 * re-sorts a returned row, and nothing defaults `lifecycle` on the client: the
 * server applies the all-populations scope when the parameter is absent and
 * echoes what it applied, so the chip the operator clears is the scope that
 * actually ran.
 */
export type UnitListingFilters = Omit<InventoryFilters, 'sort'> & {
  lifecycle?: UnitLifecycleScope
  sort?: UnitListingSort
  direction?: UnitListingDirection
  limit?: number
  offset?: number
}

export function unitListingQuery(filters: UnitListingFilters = {}): URLSearchParams {
  const { descriptionPresence: _descriptionPresence, ...unitFilters } = filters
  void _descriptionPresence
  const query = inventoryQuery(unitFilters)
  if (filters.lifecycle) query.set('lifecycle', filters.lifecycle)
  if (filters.sort) query.set('sort', filters.sort)
  if (filters.direction) query.set('direction', filters.direction)
  query.set('limit', String(filters.limit ?? 50))
  query.set('offset', String(filters.offset ?? 0))
  return query
}

export async function getUnitListing(
  filters: UnitListingFilters = {},
  signal?: AbortSignal,
): Promise<UnitListingPage> {
  return UnitListingPageSchema.parse(
    await readApiJson(
      await apiFetch(`/api/inventory/units/listing?${unitListingQuery(filters).toString()}`, { signal }),
    ),
  )
}

// Read-only server-authoritative diff. Called from the detail surfaces only, so
// the list payload never carries it.
export async function getVariantReconciliation(
  canonicalProductId: string,
  signal?: AbortSignal,
): Promise<VariantReconciliation> {
  return VariantReconciliationSchema.parse(
    await readApiJson(
      await apiFetch(`/api/inventory/${encodeURIComponent(canonicalProductId)}/reconciliation`, { signal }),
    ),
  )
}

// Read-only product media for the detail surface. The list payload never carries
// it, for the same reason it never carries reconciliation.
export async function getVariantMedia(canonicalProductId: string, signal?: AbortSignal): Promise<ProductMedia> {
  return ProductMediaSchema.parse(
    await readApiJson(
      await apiFetch(`/api/inventory/${encodeURIComponent(canonicalProductId)}/media`, { signal }),
    ),
  )
}

/**
 * The same media, addressed by the product that owns it.
 *
 * `product_media` is keyed by product, so a product whose catalogue holds no
 * legacy variant is a complete subject here and answers the typed empty body
 * rather than a variant refusal. Every surface that already knows a `productId`
 * reads through this one.
 */
export async function getProductMedia(productId: string, signal?: AbortSignal): Promise<ProductMedia> {
  return ProductMediaSchema.parse(
    await readApiJson(
      await apiFetch(`/api/inventory/products/${encodeURIComponent(productId)}/media`, { signal }),
    ),
  )
}

/**
 * Every order one piece's relations name, newest first, each with its own outcome.
 * The card asks for it on demand, so buyer names never travel on the list payload.
 */
export async function getProductOrderHistory(productId: string, signal?: AbortSignal): Promise<ProductOrderHistory> {
  return ProductOrderHistorySchema.parse(
    await readApiJson(
      await apiFetch(`/api/inventory/products/${encodeURIComponent(productId)}/orders`, { signal }),
    ),
  )
}

export class MediaWriteRefusalError extends Error {
  readonly status: number
  readonly reason: string | null

  constructor(status: number, reason: string | null, message: string) {
    super(message)
    this.name = 'MediaWriteRefusalError'
    this.status = status
    this.reason = reason
  }
}

async function readMediaWriteJson(response: Response): Promise<unknown> {
  if (response.ok) return readApiJson(response)
  let reason: string | null = null
  let message = 'Изменения не сохранены.'
  try {
    const payload: unknown = await response.clone().json()
    if (typeof payload === 'object' && payload !== null) {
      const body = payload as { reason?: unknown; message?: unknown }
      if (typeof body.reason === 'string') reason = body.reason
      if (typeof body.message === 'string') {
        const candidate = body.message.trim()
        if (candidate.length > 0 && candidate.length <= 240 && /[А-Яа-яЁё]/.test(candidate)) message = candidate
      }
    }
  } catch {
    // A proxy page is not a domain reason. Keep the transport fallback.
  }
  throw new MediaWriteRefusalError(response.status, reason, message)
}

export async function uploadProductMedia(productId: string, file: File): Promise<ProductMediaWrite> {
  return ProductMediaWriteSchema.parse(await readMediaWriteJson(await apiFetch(
    `/api/products/${encodeURIComponent(productId)}/media`,
    { method: 'POST', headers: { 'Content-Type': file.type }, body: file },
  )))
}

export async function reorderProductMedia(productId: string, mediaIds: readonly string[]): Promise<ProductMediaOrder> {
  return ProductMediaOrderSchema.parse(await readMediaWriteJson(await apiFetch(
    `/api/products/${encodeURIComponent(productId)}/media/order`,
    { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ mediaIds }) },
  )))
}

export async function makePrimaryProductMedia(productId: string, mediaId: string): Promise<ProductMediaWrite> {
  return ProductMediaWriteSchema.parse(await readMediaWriteJson(await apiFetch(
    `/api/products/${encodeURIComponent(productId)}/media/${encodeURIComponent(mediaId)}/primary`,
    { method: 'PUT' },
  )))
}

export async function deleteProductMedia(productId: string, mediaId: string): Promise<ProductMediaOrder> {
  return ProductMediaOrderSchema.parse(await readMediaWriteJson(await apiFetch(
    `/api/products/${encodeURIComponent(productId)}/media/${encodeURIComponent(mediaId)}`,
    { method: 'DELETE' },
  )))
}

/**
 * The other articles of this variant's model - the variation feed.
 *
 * Read-only, and the detail surface asks for it separately, for the same reason
 * media and reconciliation are separate: the list payload carries none of it.
 * Everything the strip renders is decided on the server, including the
 * differentiator string and the per-account publication states; the schema
 * refuses a body that collapses an unknown model into an empty member list.
 */
export async function getVariantModelGroup(canonicalProductId: string, signal?: AbortSignal): Promise<ModelGroup> {
  return ModelGroupSchema.parse(
    await readApiJson(
      await apiFetch(`/api/inventory/${encodeURIComponent(canonicalProductId)}/model-group`, { signal }),
    ),
  )
}

/** Media bytes a named reason refused, carrying the server's Russian sentence. */
export class MediaBytesUnavailableError extends Error {
  readonly status: number
  readonly reason: string | null

  constructor(status: number, reason: string | null, message: string) {
    super(message)
    this.name = 'MediaBytesUnavailableError'
    this.status = status
    this.reason = reason
  }
}

/**
 * Fetch one media object's bytes as an object URL.
 *
 * `mediaUrl` is a Clerk-protected `/api/` path, so a bare `<img src>` gets 401
 * and renders as a broken image with nothing to report. The bytes are fetched
 * with the bearer token and handed to the DOM as a blob URL instead; the caller
 * owns revoking it.
 */
export async function readMediaObjectUrl(mediaUrl: string, signal?: AbortSignal): Promise<string> {
  const response = await apiFetch(mediaUrl, { signal })
  if (!response.ok) {
    // The byte route answers `{ reason, message }` with a server-owned Russian
    // message; anything else falls through to the shared generic sentence.
    let reason: string | null = null
    let message = 'Не удалось загрузить файл.'
    try {
      const payload: unknown = await response.clone().json()
      if (typeof payload === 'object' && payload !== null) {
        const body = payload as { reason?: unknown; message?: unknown }
        if (typeof body.reason === 'string') reason = body.reason
        if (typeof body.message === 'string') {
          const candidate = body.message.trim()
          if (candidate.length > 0 && candidate.length <= 240 && /[А-Яа-яЁё]/.test(candidate)) message = candidate
        }
      }
    } catch {
      // Keep the generic sentence rather than inventing a classification.
    }
    throw new MediaBytesUnavailableError(response.status, reason, message)
  }
  return URL.createObjectURL(await response.blob())
}
